import { useEffect, useState } from "react";
import { MyFile, api } from "../api";
import { useAuth } from "../auth";
import { ConfirmModal } from "../components/ConfirmModal";

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function MyFilesPage() {
  const { token } = useAuth();
  const [files, setFiles] = useState<MyFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<MyFile | null>(null);

  const reload = () => {
    if (!token) return;
    api
      .listMyFiles(token)
      .then(setFiles)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  const onDownload = async (f: MyFile) => {
    if (!token) return;
    setBusyKey(f.key);
    try {
      const { blob, filename } = await api.downloadMyFile(token, f.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(String((e as { message?: string })?.message ?? e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">My Files</h2>
      <p className="text-sm text-slate-600">
        Large query results you exported from Claude (via <code>export_query</code>) are saved here as CSV. Files are
        private to your account, encrypted at rest, and kept for 180 days. Downloads stream directly through the
        portal — no shareable links are created.
      </p>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="card overflow-hidden">
        {files === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : files.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            No files yet. In Claude, ask it to export a large query — it will appear here.
          </p>
        ) : (
          <table className="table-clean w-full">
            <thead>
              <tr>
                <th>file</th>
                <th className="text-right">size</th>
                <th>created</th>
                <th className="text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.key} className="border-t border-slate-100 align-middle">
                  <td className="font-mono text-xs truncate max-w-[20rem]" title={f.name}>{f.name}</td>
                  <td className="text-right text-xs text-slate-500">{fmtSize(f.size)}</td>
                  <td className="text-xs text-slate-500">
                    {f.last_modified ? new Date(f.last_modified).toLocaleString() : "—"}
                  </td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      className="btn-primary text-xs py-1"
                      disabled={busyKey === f.key}
                      onClick={() => void onDownload(f)}
                    >
                      download
                    </button>
                    <button
                      type="button"
                      className="btn-danger text-xs py-1"
                      disabled={busyKey === f.key}
                      onClick={() => setToDelete(f)}
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {token && toDelete && (
        <ConfirmModal
          title="Delete file"
          tone="danger"
          confirmLabel="Delete"
          onClose={() => setToDelete(null)}
          onConfirm={async () => {
            await api.deleteMyFile(token, toDelete.key);
            reload();
          }}
          body={
            <p>
              Delete <strong>{toDelete.name}</strong>? This permanently removes the exported file from your storage.
            </p>
          }
        />
      )}
    </div>
  );
}
